import "./InspectionRequests.scss";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { Modal, Box } from "@mui/material";
import { InspectionColumns } from "../../SourceData";
import api from "../../utils/api";
import BlueButton from "../../components/BlueButton/BlueButton";
import ColorButton from "../../components/ColorButton/ColorButton";
import RejectGif from "./../../assets/reject-gif.gif";
import CloseIcon from "./../../assets/close-icon.svg";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { setNotificationCount } from "../../redux/notificationsSlice";
import { usePermissions } from "./../../hooks/usePermissions";

const InspectionRequests = () => {
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const notificationCount = useSelector(
    (state) => state.notifications?.counts?.previewRequests || 0
  );

  const fetchRequests = async () => {
    // Check view permission before fetching data
    if (!hasPermission(131)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
        status: status,
      };

      const response = await api.get("/preview-requests", { params });

      if (response.data.status === "success") {
        const data = response.data.data;
        const totalCount = response.data.totalCount || 0;

        setRequests(data);
        setTotalCount(totalCount);
        const pendingCount = status === "pending" ? totalCount : 0;
        dispatch(
          setNotificationCount({
            key: "previewRequests",
            count: pendingCount,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching preview requests:", error);
      toast.error(error.message || "حدث خطأ أثناء جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch requests when dependencies change
  useEffect(() => {
    fetchRequests();
  }, [page, pageSize, status]); // Added status to dependencies

  // Effect to refetch when notification count changes (triggered by WebSocket middleware)
  useEffect(() => {
    // Only refetch if we're on this page and the count changed
    if (notificationCount > 0 && totalCount !== notificationCount) {
      console.log(
        "Preview requests notification count changed via WebSocket, refreshing data..."
      );
      fetchRequests();
    }
  }, [notificationCount]);

  const handleOpenModal = (request) => {
    // Check delete permission before opening modal
    if (!hasPermission(70)) {
      toast.error("ليس لديك صلاحية لحذف الطلبات");
      return;
    }

    setSelectedRequest(request);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
  };

  const handleRefund = async () => {
    if (!selectedRequest || processingRefund) return;

    // Double-check delete permission before processing
    if (!hasPermission(70)) {
      toast.error("ليس لديك صلاحية لحذف الطلبات");
      return;
    }

    setProcessingRefund(true);

    try {
      // Close the request by sending status: false
      const response = await api.patch(
        `/preview-requests/${selectedRequest._id}`,
        {
          status: false,
        }
      );

      if (response.data.status === "success") {
        toast.success("تم اغلاق المعاينة بنجاح");
        handleCloseModal();
        fetchRequests(); // This will refresh data and update notification count
      } else {
        toast.error("فشل في إغلاق المعاينة");
      }
    } catch (error) {
      console.error("Error closing request:", error);
      toast.error(error.message || "حدث خطأ أثناء إغلاق المعاينة");
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleCreateSubscription = (request) => {
    // Check approve permission before creating subscription
    if (!hasPermission(71)) {
      toast.error("ليس لديك صلاحية للموافقة على الطلبات");
      return;
    }

    setSelectedRequest(request);
    setIsOpen(true);
  };

  // Function to handle successful operations that should refresh data
  const handleOperationSuccess = () => {
    fetchRequests();
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "الإجراءات",
      minWidth: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // Check permissions for each action
        const canDelete = hasPermission(70);
        const canApprove = hasPermission(71);

        return (
          <div className="cell-action">
            <ColorButton
              text="اغلاق الطلب"
              size="medium"
              variant="outlined"
              color="red"
              onClick={() => handleOpenModal(params.row)}
              disabled={!canDelete}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
              title={canDelete ? "اغلاق الطلب" : "ليس لديك صلاحية لحذف الطلبات"}
            />
            <BlueButton
              text="انشاء اشتراك"
              size="medium"
              variant="fill"
              onClick={() => handleCreateSubscription(params.row)}
              disabled={!canApprove}
              style={{
                cursor: canApprove ? "pointer" : "not-allowed",
                opacity: canApprove ? 1 : 0.5,
              }}
              title={
                canApprove
                  ? "انشاء اشتراك"
                  : "ليس لديك صلاحية للموافقة على الطلبات"
              }
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          طلبات المعاينة
          {notificationCount > 0 && (
            <span className="notification-badge">({notificationCount})</span>
          )}
        </p>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={InspectionColumns.concat(actionColumn)}
          rows={requests.map((request, index) => ({
            ...request,
            // Generate unique id if not present
            id: request._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "طلب في الصفحة",
            },
          }}
        />
      </div>

      {modalOpen && hasPermission(70) && (
        <Modal
          open={modalOpen && selectedRequest !== null}
          onClose={!processingRefund ? handleCloseModal : undefined}
          aria-labelledby="refund-confirmation-modal"
          aria-describedby="modal-to-confirm-refund-action"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <div
              className="modal-header"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <img
                src={CloseIcon}
                alt="إغلاق"
                onClick={!processingRefund ? handleCloseModal : undefined}
                style={{ cursor: "pointer", width: "24px", height: "24px" }}
              />
            </div>

            <div
              className="modal-body"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "1rem 0",
              }}
            >
              <img
                src={RejectGif}
                alt="رفض"
                style={{ maxWidth: "150px", marginBottom: "1rem" }}
              />
              <h4>هل أنت متأكد من إغلاق طلب المعاينة؟</h4>
            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <BlueButton
                text={processingRefund ? "جاري التنفيذ..." : "تأكيد"}
                onClick={handleRefund}
                size="medium"
                variant="fill"
                disabled={processingRefund}
              />
              <ColorButton
                text="إلغاء"
                onClick={handleCloseModal}
                size="medium"
                variant="outlined"
                color="gray"
                disabled={processingRefund}
              />
            </div>
          </Box>
        </Modal>
      )}

      {isOpen && hasPermission(71) && (
        <SwipeableModals
          open={isOpen}
          onClose={() => setIsOpen(false)}
          onOpen={() => setIsOpen(true)}
          currentView={"createSubscription"}
          requestId={selectedRequest?._id} // Pass the request ID
          onSuccess={handleOperationSuccess} // Use the helper function
        />
      )}
    </div>
  );
};

export default InspectionRequests;
